import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useState } from 'react';
import { getLocationsWithAddress } from '../../utils/api';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import L from 'leaflet';
import { useData } from '../../hooks/useData';

// Configure default Leaflet icon so markers display correctly
const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

export default function AddressMapPage() {
  const { profile } = useData();
  const [coords, setCoords] = useState<
    { lat: number; lng: number; address: string; locationName: string }[]
  >([]);

  useEffect(() => {
    async function fetchCoords(): Promise<void> {
      if (!profile?.locations || profile.locations.length === 0) {
        console.warn('No locations found in profile.');
        return;
      }
    
      console.info(`Found ${profile.locations.length} locations. Beginning address lookup.`);
    
      // Use getLocationsWithAddress if it modifies or filters locations, or use directly:
      const response = await getLocationsWithAddress(profile.locations);
      const locations = Array.isArray(response) ? response : response.locations;

    
      const geocoded = await Promise.all(locations.map(async (loc: any) => {
        const cleanedAddress = loc.address
          .split(',')
          .map(part => part.trim())
          .filter(part => part.toLowerCase() !== 'null' && part !== '')
          .join(', ');
      
        const query = encodeURIComponent(cleanedAddress);
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${query}`;
      
        try {
          const response = await fetch(url, {
            headers: {
              'User-Agent': 'YourAppName/1.0 (your@email.com)' // Replace with real email
            }
          });
          const data = await response.json();
      
          if (data.length > 0) {
            const { lat, lon } = data[0];
            return {
              lat: parseFloat(lat),
              lng: parseFloat(lon),
              address: cleanedAddress,
              locationName: loc.locationName
            };
          }
      
          // If full address fails, try just city + state fallback
          const cityState = cleanedAddress.split(',').slice(-2).join(', ');
          const fallbackQuery = encodeURIComponent(cityState);
          const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${fallbackQuery}`;
      
          const fallbackResponse = await fetch(fallbackUrl, {
            headers: {
              'User-Agent': 'YourAppName/1.0 (your@email.com)'
            }
          });
          const fallbackData = await fallbackResponse.json();
      
          if (fallbackData.length > 0) {
            const { lat, lon } = fallbackData[0];
            return {
              lat: parseFloat(lat),
              lng: parseFloat(lon),
              address: cityState + ' (approx)',
              locationName: loc.locationName
            };
          }
      
          console.warn(`No coordinates found for: ${cleanedAddress}`);
          return null;
      
        } catch (err) {
          console.error(`Error geocoding address ${cleanedAddress}:`, err);
          return null;
        }
      }));
      
       
    
      const filtered = geocoded.filter(Boolean) as {
        lat: number;
        lng: number;
        address: string;
        locationName: string;
      }[];
    
      setCoords(filtered);
    }
    
    

    fetchCoords();
  }, [profile]);

  return (
    <div className="w-screen h-screen">
      <MapContainer center={[37.0902, -95.7129]} zoom={4} className="w-full h-full z-0">
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {coords.map((point, idx) => (
          <Marker key={idx} position={[point.lat, point.lng]}>
            <Popup>
              <div>
                <strong>{point.locationName}</strong>
                <br />
                {point.address}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
